import { useState } from "react";
import { Button, Textarea, Tooltip } from "@fluentui/react-components";
import { ThumbLike20Regular, ThumbLike20Filled, ThumbDislike20Regular, ThumbDislike20Filled, Send20Regular } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";

import styles from "./Feedback.module.css";
import { submitFeedbackApi } from "../../api";
import { getToken } from "../../authConfig";
import { useOidc } from "../../oidcContext";

interface Props {
    traceId?: string;
}

type Rating = "up" | "down";

export const Feedback = ({ traceId }: Props) => {
    const { t } = useTranslation();
    const { userManager: client } = useOidc();

    const [rating, setRating] = useState<Rating | undefined>(undefined);
    const [showComment, setShowComment] = useState(false);
    const [comment, setComment] = useState("");
    const [submitted, setSubmitted] = useState(false);

    // Feedback requires a trace id from the backend (only present when Langfuse is
    // enabled). Without it there is nothing to attach feedback to, so hide the UI.
    if (!traceId) {
        return null;
    }

    const send = async (nextRating: Rating, withComment: string) => {
        const idToken = client ? await getToken(client) : undefined;
        try {
            await submitFeedbackApi({ trace_id: traceId, positive: nextRating === "up", comment: withComment || undefined }, idToken);
        } catch (e) {
            console.error("Failed to submit feedback", e);
        }
    };

    const handleRating = (nextRating: Rating) => {
        // Clicking the already-selected thumb collapses the comment box again.
        if (rating === nextRating) {
            setShowComment(prev => !prev);
            return;
        }
        setRating(nextRating);
        setShowComment(true);
        setSubmitted(false);
        // Fire the thumb rating immediately; the comment can follow separately.
        void send(nextRating, comment);
    };

    const handleSubmitComment = async () => {
        if (!rating) {
            return;
        }
        await send(rating, comment);
        setSubmitted(true);
        setShowComment(false);
    };

    return (
        <div className={styles.container}>
            <div className={styles.buttons}>
                <Tooltip content={t("feedback.thumbUp")} relationship="label">
                    <Button
                        appearance="transparent"
                        size="small"
                        className={styles.thumb}
                        icon={rating === "up" ? <ThumbLike20Filled className={styles.active} /> : <ThumbLike20Regular />}
                        aria-label={t("feedback.thumbUp")}
                        onClick={() => handleRating("up")}
                    />
                </Tooltip>
                <Tooltip content={t("feedback.thumbDown")} relationship="label">
                    <Button
                        appearance="transparent"
                        size="small"
                        className={styles.thumb}
                        icon={rating === "down" ? <ThumbDislike20Filled className={styles.active} /> : <ThumbDislike20Regular />}
                        aria-label={t("feedback.thumbDown")}
                        onClick={() => handleRating("down")}
                    />
                </Tooltip>
                {submitted && <span className={styles.thanks}>{t("feedback.thanks")}</span>}
            </div>

            {showComment && (
                <div className={styles.commentBox}>
                    <Textarea
                        className={styles.textarea}
                        value={comment}
                        placeholder={t("feedback.commentPlaceholder")}
                        onChange={(_, data) => setComment(data.value)}
                        resize="vertical"
                    />
                    <p className={styles.privacyHint}>{t("feedback.privacyHint")}</p>
                    <Button appearance="outline" size="small" icon={<Send20Regular />} onClick={handleSubmitComment} className={styles.submit}>
                        {t("feedback.submit")}
                    </Button>
                </div>
            )}
        </div>
    );
};
